import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Mail, Phone, Building, Video, MapPin, CheckCircle } from 'lucide-react';
import { supabase, TABLES } from '../../config/supabase';
import GoogleCalendarService from '../../services/googleCalendarService';
import logger from '../../utils/logger';

const PublicBookingWidget = ({ 
  userId = null, 
  availabilitySettings = {
    startHour: 9,
    endHour: 17,
    slotDuration: 30,
    workingDays: [1, 2, 3, 4, 5], // Monday to Friday
    bufferTime: 15
  },
  customization = {
    title: 'Book a Meeting',
    description: 'Choose a time that works for you',
    primaryColor: '#2563eb',
    companyName: 'CREATILLA'
  }
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    meetingType: 'video'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [userTimezone, setUserTimezone] = useState('');
  const [timezoneInfo, setTimezoneInfo] = useState('');
  const [slotsCache, setSlotsCache] = useState(new Map()); // Cache for performance

  // Get user's timezone on component mount
  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimezone(timezone);
    
    // Calculate time difference with Helsinki
    const now = new Date();
    const userTime = now.toLocaleString('en-US', { timeZone: timezone, hour12: false });
    const helsinkiTime = now.toLocaleString('en-US', { timeZone: 'Europe/Helsinki', hour12: false });
    
    const userHour = parseInt(userTime.split(' ')[1].split(':')[0]);
    const helsinkiHour = parseInt(helsinkiTime.split(' ')[1].split(':')[0]);
    const timeDiff = helsinkiHour - userHour;
    
    let diffText = '';
    if (timeDiff > 0) {
      diffText = `Helsinki is ${timeDiff} hours ahead of your time`;
    } else if (timeDiff < 0) {
      diffText = `Helsinki is ${Math.abs(timeDiff)} hours behind your time`;
    } else {
      diffText = 'Helsinki is in the same timezone as you';
    }
    
    setTimezoneInfo(diffText);
  }, []);

  // Generate available time slots for a given date
  const generateTimeSlots = (date, existingMeetings = [], googleEvents = []) => {
    const slots = [];
    const dayOfWeek = date.getDay();
    
    // Check if it's a working day
    if (!availabilitySettings.workingDays.includes(dayOfWeek)) {
      return slots;
    }

    const { startHour, endHour, slotDuration, bufferTime } = availabilitySettings;
    
    // Generate all possible slots
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += slotDuration) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        
        // Check if slot conflicts with existing meetings
        const isBooked = existingMeetings.some(meeting => {
          const meetingTime = meeting.meeting_time.substring(0, 5);
          const meetingDuration = meeting.duration || 30;
          
          // Check for time overlap including buffer
          const slotStart = hour * 60 + minute;
          const slotEnd = slotStart + slotDuration;
          const meetingStart = parseInt(meetingTime.split(':')[0]) * 60 + parseInt(meetingTime.split(':')[1]);
          const meetingEnd = meetingStart + meetingDuration;
          
          return (slotStart < meetingEnd + bufferTime) && (slotEnd + bufferTime > meetingStart);
        });
        
        // Check if slot conflicts with Google Calendar events
        const isGoogleBooked = googleEvents.some(event => {
          if (!event.start?.dateTime) return false;
          
          const eventStart = new Date(event.start.dateTime);
          const eventEnd = new Date(event.end?.dateTime || event.start.dateTime);
          
          const slotStart = new Date(date);
          slotStart.setHours(hour, minute, 0, 0);
          const slotEnd = new Date(slotStart.getTime() + slotDuration * 60000);
          
          return (slotStart < new Date(eventEnd.getTime() + bufferTime * 60000)) && 
                 (new Date(slotEnd.getTime() + bufferTime * 60000) > eventStart);
        });
        
        if (!isBooked && !isGoogleBooked) {
          slots.push(timeString);
        }
      }
    }
    
    return slots;
  };

  // Load available time slots for selected date - ULTRA OPTIMIZED
  const loadAvailableSlots = async (date) => {
    if (!date) return;
    
    const dateString = date.toISOString().split('T')[0];
    
    // PERFORMANCE BOOST: Check cache first
    if (slotsCache.has(dateString)) {
      logger.log('📋 Using cached slots for:', dateString);
      setAvailableSlots(slotsCache.get(dateString));
      return;
    }
    
    setLoadingSlots(true);
    
    try {
      // ULTRA PERFORMANCE FIX: Show CRM slots immediately, then update with Google Calendar
      logger.log('🚀 Fast loading: Getting CRM meetings first...');
      
      // Step 1: Get CRM meetings quickly and show initial slots
      const { data: meetings, error: meetingsError } = await supabase
        .from(TABLES.MEETINGS)
        .select('meeting_time, duration')
        .eq('meeting_date', dateString);
      
      if (meetingsError) {
        logger.warn('Error fetching meetings:', meetingsError);
      }
      
      // Generate initial slots with just CRM data (fast)
      const initialSlots = generateTimeSlots(date, meetings || [], []);
      setAvailableSlots(initialSlots);
      logger.log('⚡ Initial slots loaded (CRM only):', initialSlots.length, 'slots');
      
      // Step 2: Get Google Calendar events in background (if enabled)
      if (GoogleCalendarService.isEnabled()) {
        logger.log('📅 Loading Google Calendar events in background...');
        
        try {
          const googleEvents = await GoogleCalendarService.getEventsForDate(date);
          
          // Regenerate slots with both CRM and Google Calendar data
          const finalSlots = generateTimeSlots(date, meetings || [], googleEvents);
          
          // Only update if slots actually changed
          if (JSON.stringify(finalSlots) !== JSON.stringify(initialSlots)) {
            setAvailableSlots(finalSlots);
            logger.log('🔄 Updated slots with Google Calendar:', finalSlots.length, 'slots');
          } else {
            logger.log('✅ No Google Calendar conflicts found');
          }
          
          // Cache the final results
          const newCache = new Map(slotsCache);
          newCache.set(dateString, finalSlots);
          setSlotsCache(newCache);
          
        } catch (googleError) {
          logger.warn('Google Calendar error (using CRM-only slots):', googleError);
          // Keep the CRM-only slots if Google Calendar fails
        }
      } else {
        // Cache CRM-only results if Google Calendar is disabled
        const newCache = new Map(slotsCache);
        newCache.set(dateString, initialSlots);
        setSlotsCache(newCache);
      }
      
      // Clear old cache entries (keep only last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const cutoffDate = sevenDaysAgo.toISOString().split('T')[0];
      
      const currentCache = slotsCache;
      for (const [cachedDate] of currentCache) {
        if (cachedDate < cutoffDate) {
          currentCache.delete(cachedDate);
        }
      }
      
      logger.log('✅ Slot loading complete for:', dateString);
      
    } catch (error) {
      logger.error('Error loading available slots:', error);
      setError('Failed to load available time slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  // Handle date selection
  const handleDateChange = (e) => {
    // Create date in Helsinki timezone to avoid timezone conversion issues
    const dateValue = e.target.value; // YYYY-MM-DD format
    const [year, month, day] = dateValue.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0, 0); // Set to noon to avoid timezone issues
    setSelectedDate(date);
    setSelectedTime(null);
    loadAvailableSlots(date);
  };

  // SECURITY: Sanitize user input to prevent injection attacks
  const sanitizeInput = (str) => {
    if (!str) return '';
    return str
      .replace(/[<>]/g, '')           // Strip HTML angle brackets
      .replace(/javascript:/gi, '')    // Strip javascript: protocol
      .replace(/on\w+=/gi, '')         // Strip inline event handlers
      .trim()
      .slice(0, 500);                  // Limit length
  };

  // SECURITY: Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) && email.length <= 254;
  };

  // SECURITY: Validate phone format
  const isValidPhone = (phone) => {
    if (!phone) return true; // Phone is optional
    const phoneRegex = /^[+]?[\d\s\-().]{0,20}$/;
    return phoneRegex.test(phone);
  };

  // Handle booking submission
  const handleBooking = async () => {
    if (!selectedDate || !selectedTime || !bookingForm.name || !bookingForm.email) {
      setError('Please fill in all required fields');
      return;
    }

    // SECURITY: Validate email format
    if (!isValidEmail(bookingForm.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // SECURITY: Validate phone if provided
    if (bookingForm.phone && !isValidPhone(bookingForm.phone)) {
      setError('Please enter a valid phone number');
      return;
    }

    // SECURITY: Validate name length
    if (bookingForm.name.trim().length < 2 || bookingForm.name.trim().length > 100) {
      setError('Name must be between 2 and 100 characters');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // SECURITY: Sanitize all user inputs before database operations
      const sanitizedForm = {
        name: sanitizeInput(bookingForm.name),
        email: bookingForm.email.trim().toLowerCase(),
        phone: sanitizeInput(bookingForm.phone),
        company: sanitizeInput(bookingForm.company),
        message: sanitizeInput(bookingForm.message),
        meetingType: bookingForm.meetingType
      };

      logger.log('🔍 Checking for existing lead:', sanitizedForm.email);
      
      // STEP 1: Check if lead already exists
      const { data: existingLead, error: searchError } = await supabase
        .from(TABLES.LEADS)
        .select('*')
        .eq('email', sanitizedForm.email)
        .maybeSingle();

      let lead;
      let isExistingLead = false;

      if (existingLead && !searchError) {
        // EXISTING LEAD FOUND - Update with bonus score
        const { data: updatedLead, error: updateError } = await supabase
          .from(TABLES.LEADS)
          .update({
            first_name: sanitizedForm.name.split(' ')[0] || existingLead.first_name,
            company: sanitizedForm.company || existingLead.company,
            score: (existingLead.score || 0) + 25,
            status: 'interested',
            updated_at: new Date().toISOString()
          })
          .eq('email', sanitizedForm.email)
          .select()
          .single();

        if (updateError) throw updateError;
        lead = updatedLead;
        isExistingLead = true;
        
        // Update contact details
        await supabase
          .from(TABLES.CONTACT_DETAILS)
          .upsert({
            lead_email: lead.email,
            lead_id: lead.id,
            last_name: sanitizedForm.name.split(' ').slice(1).join(' '),
            phone: sanitizedForm.phone,
            updated_at: new Date().toISOString()
          }, { onConflict: 'lead_email' });
        
      } else {
        // NEW LEAD - Create fresh lead
        const { data: newLead, error: createError } = await supabase
          .from(TABLES.LEADS)
          .insert([{
            email: sanitizedForm.email,
            first_name: sanitizedForm.name.split(' ')[0],
            company: sanitizedForm.company,
            source: 'Meeting Booked',
            status: 'interested',
            score: 20,
            created_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (createError) throw createError;
        lead = newLead;
        isExistingLead = false;
        
        // Create contact details
        await supabase
          .from(TABLES.CONTACT_DETAILS)
          .insert([{
            lead_email: lead.email,
            lead_id: lead.id,
            last_name: sanitizedForm.name.split(' ').slice(1).join(' '),
            phone: sanitizedForm.phone,
            created_at: new Date().toISOString()
          }]);
      }

      // STEP 2: Create the meeting with CORRECT timezone conversion
      const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const helsinkiTimezone = 'Europe/Helsinki';
      
      // No double conversion - store Helsinki time directly
      const [selectedHour, selectedMinute] = selectedTime.split(':').map(Number);
      
      // Get date components
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      const day = selectedDate.getDate();
      
      let helsinkiDateStr, helsinkiTimeFormatted;
      
      // Check if user is already in Helsinki timezone
      if (clientTimezone === 'Europe/Helsinki') {
        // User is in Helsinki - no conversion needed, store their time directly
        helsinkiDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        helsinkiTimeFormatted = `${String(selectedHour).padStart(2, '0')}:${String(selectedMinute).padStart(2, '0')}`;
        
        logger.log('🇫🇮 HELSINKI USER - No conversion needed:', {
          clientTimezone,
          selectedTime,
          storedTime: helsinkiTimeFormatted,
          storedDate: helsinkiDateStr
        });
      } else {
        // User is NOT in Helsinki - convert once and store the converted time
        const tempDate = new Date(year, month - 1, day, 12, 0, 0, 0);
        
        const clientTimeAtDate = tempDate.toLocaleString('sv-SE', {
          timeZone: clientTimezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        
        const helsinkiTimeAtDate = tempDate.toLocaleString('sv-SE', {
          timeZone: 'Europe/Helsinki',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        
        const clientHourAtDate = parseInt(clientTimeAtDate.split(':')[0]);
        const helsinkiHourAtDate = parseInt(helsinkiTimeAtDate.split(':')[0]);
        const hourDifference = helsinkiHourAtDate - clientHourAtDate;
        
        // Apply conversion once
        const helsinkiHour = selectedHour + hourDifference;
        let finalHelsinkiHour = helsinkiHour;
        let finalDay = day;
        let finalMonth = month;
        let finalYear = year;
        
        // Handle day boundary crossings
        if (finalHelsinkiHour >= 24) {
          finalHelsinkiHour -= 24;
          finalDay += 1;
          const daysInMonth = new Date(finalYear, finalMonth, 0).getDate();
          if (finalDay > daysInMonth) {
            finalDay = 1;
            finalMonth += 1;
            if (finalMonth > 12) {
              finalMonth = 1;
              finalYear += 1;
            }
          }
        } else if (finalHelsinkiHour < 0) {
          finalHelsinkiHour += 24;
          finalDay -= 1;
          if (finalDay < 1) {
            finalMonth -= 1;
            if (finalMonth < 1) {
              finalMonth = 12;
              finalYear -= 1;
            }
            finalDay = new Date(finalYear, finalMonth, 0).getDate();
          }
        }
        
        // Store the converted Helsinki time
        helsinkiDateStr = `${finalYear}-${String(finalMonth).padStart(2, '0')}-${String(finalDay).padStart(2, '0')}`;
        helsinkiTimeFormatted = `${String(finalHelsinkiHour).padStart(2, '0')}:${String(selectedMinute).padStart(2, '0')}`;
        
        logger.log('🌍 INTERNATIONAL USER - Converted once:', {
          clientTimezone,
          clientTime: selectedTime,
          clientDate: `${year}-${month}-${day}`,
          hourDifference,
          convertedTime: helsinkiTimeFormatted,
          convertedDate: helsinkiDateStr,
          calculation: `${selectedTime} (${clientTimezone}) → ${helsinkiTimeFormatted} (Helsinki)`
        });
      }
      
      // Calculate timezone difference for display (simplified)
      const clientHour = selectedHour;
      const displayHelsinkiHour = parseInt(helsinkiTimeFormatted.split(':')[0]);
      let timeDifference = displayHelsinkiHour - clientHour;
      
      // Handle day boundary crossings
      if (timeDifference > 12) timeDifference -= 24;
      if (timeDifference < -12) timeDifference += 24;
      
      const timeDiffText = timeDifference > 0 ? `${timeDifference} hours ahead` : timeDifference < 0 ? `${Math.abs(timeDifference)} hours behind` : 'same time';
      
      logger.log('✅ TIMEZONE CONVERSION COMPLETE:', {
        userType: clientTimezone === 'Europe/Helsinki' ? 'Helsinki User' : 'International User',
        clientTimezone,
        selectedTime,
        storedTime: helsinkiTimeFormatted,
        storedDate: helsinkiDateStr,
        timeDifference: `Helsinki is ${timeDiffText} compared to client`
      });
      
      const meetingData = {
        lead_id: lead.id,
        lead_email: lead.email,
        title: `Meeting with ${sanitizedForm.name}`,
        description: sanitizedForm.message || 'Meeting booked via public calendar widget',
        meeting_date: helsinkiDateStr, // Use converted Helsinki date
        meeting_time: helsinkiTimeFormatted, // Store in Helsinki timezone
        duration: availabilitySettings.slotDuration || 30,
        meeting_type: sanitizedForm.meetingType,
        status: 'scheduled',
        calendar_sync_status: 'not_synced',
        client_timezone: clientTimezone, // Store client's original timezone for reference
        client_time: selectedTime // Store client's original time for reference
      };
      
      logger.log('📝 DATABASE INSERT DEBUG:', {
        meetingData,
        storingDate: helsinkiDateStr,
        storingTime: helsinkiTimeFormatted,
        expectedTime: '08:00 for 11:00 AM Bangladesh booking'
      });

      const { data: meeting, error: meetingError } = await supabase
        .from(TABLES.MEETINGS)
        .insert([meetingData])
        .select()
        .single();

      if (meetingError) throw meetingError;

      // STEP 3: Create lead event with appropriate scoring
      const eventData = {
        lead_email: lead.email,
        event_type: isExistingLead ? 'return_booking' : 'first_booking',
        score_delta: isExistingLead ? 25 : 20, // Higher score for returning customers
        campaign_name: 'Meeting Booked', // Fixed campaign name
        event_data: JSON.stringify({
          meeting_id: meeting.id,
          booking_type: 'public_widget',
          meeting_type: sanitizedForm.meetingType,
          is_existing_lead: isExistingLead,
          client_timezone: clientTimezone,
          timezone_conversion: `${selectedTime} (${clientTimezone}) → ${helsinkiTimeFormatted} (${helsinkiTimezone})`
        })
      };

      await supabase
        .from(TABLES.LEAD_EVENTS)
        .insert([eventData]);

      // STEP 4: Sync to Google Calendar if enabled
      if (GoogleCalendarService.isEnabled()) {
        try {
          await GoogleCalendarService.createEvent({
            ...meetingData,
            id: meeting.id,
            contact_name: sanitizedForm.name,
            contact_email: sanitizedForm.email
          });
          
          // Update meeting sync status
          await supabase
            .from(TABLES.MEETINGS)
            .update({ calendar_sync_status: 'synced' })
            .eq('id', meeting.id);
        } catch (syncError) {
          logger.warn('Google Calendar sync failed:', syncError);
          await supabase
            .from(TABLES.MEETINGS)
            .update({ calendar_sync_status: 'sync_failed' })
            .eq('id', meeting.id);
        }
      }

      setSuccess(true);
      setSuccessMessage(isExistingLead 
        ? `Welcome back, ${lead.first_name}! Your meeting has been scheduled.`
        : `Thank you, ${lead.first_name}! Your meeting has been scheduled.`
      );
      
    } catch (error) {
      logger.error('❌ Booking error:', error);
      setError('Error creating booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get maximum date (30 days from now by default)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + (availabilitySettings.maxAdvanceBooking || 30));
    return maxDate.toISOString().split('T')[0];
  };

  if (success) {
    return (
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4 backdrop-blur-sm animate-bounce">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-3xl font-bold mb-2 tracking-tight">
            Meeting Booked Successfully!
          </h3>
          <p className="text-green-100 text-lg opacity-90">
            {successMessage}
          </p>
        </div>
        
        {/* Meeting Details */}
        <div className="p-8">
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 mb-6 border border-gray-200">
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedDate?.toLocaleDateString('en-US', { 
                    weekday: 'long',
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 font-medium">Your time:</span>
                    <span className="font-bold text-lg text-blue-600">{selectedTime} ({userTimezone})</span>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-purple-600" />
                      <span className="text-gray-600 font-medium">Helsinki time:</span>
                    </div>
                    <span className="font-bold text-lg text-purple-600">
                         {(() => {
                        // ULTIMATE FIX: No double conversion for UI preview
                        const [hour, minute] = selectedTime.split(':').map(Number);
                        const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                        
                        // If user is in Helsinki, show their time directly
                        if (clientTz === 'Europe/Helsinki') {
                          return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                        }
                        
                        // If user is not in Helsinki, convert once for preview
                        const year = selectedDate.getFullYear();
                        const month = selectedDate.getMonth() + 1;
                        const day = selectedDate.getDate();
                        
                        const tempDate = new Date(year, month - 1, day, 12, 0, 0, 0);
                        
                        const clientTimeAtDate = tempDate.toLocaleString('sv-SE', {
                          timeZone: clientTz,
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        });
                        
                        const helsinkiTimeAtDate = tempDate.toLocaleString('sv-SE', {
                          timeZone: 'Europe/Helsinki',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        });
                        
                        const clientHourAtDate = parseInt(clientTimeAtDate.split(':')[0]);
                        const helsinkiHourAtDate = parseInt(helsinkiTimeAtDate.split(':')[0]);
                        const hourDifference = helsinkiHourAtDate - clientHourAtDate;
                        
                        const helsinkiHour = hour + hourDifference;
                        let finalHour = helsinkiHour >= 24 ? helsinkiHour - 24 : helsinkiHour < 0 ? helsinkiHour + 24 : helsinkiHour;
                        
                        return `${String(finalHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                      })()} (Europe/Helsinki)
                       </span>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Video className="w-5 h-5 mr-2 text-green-600" />
                      <span className="text-gray-600 font-medium">Meeting Type:</span>
                    </div>
                    <span className="font-bold text-lg text-green-600 capitalize">{bookingForm.meetingType} Meeting</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
              <span className="text-2xl">📧</span>
            </div>
            <p className="text-gray-600 font-medium">
              You'll receive a confirmation email shortly with meeting details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200 relative">
      {/* Professional Header */}
      <div className="bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 p-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-primary-700/10"></div>
        <div className="relative text-center z-10">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 rounded-xl mb-3 backdrop-blur-sm">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold mb-2 tracking-tight">
            {customization.title}
          </h1>
          <p className="text-primary-100 opacity-95">
            {customization.description}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Professional Info Section */}
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-3 mb-5">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 mb-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-sm">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-base font-semibold text-primary-700">
                  Schedule Your Meeting
                </h3>
                <p className="text-primary-600 text-xs">Choose a convenient time for your consultation</p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg animate-pulse">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-red-600 text-sm">⚠️</span>
              </div>
              <p className="text-red-700 font-medium">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-8">
          {/* Date Selection */}
          <div className="group mb-8">
            <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-primary-600" />
              Select Date *
            </label>
            <input
              type="date"
              min={getMinDate()}
              max={getMaxDate()}
              onChange={handleDateChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all duration-200 font-medium bg-gray-50 hover:bg-white group-hover:border-gray-300 shadow-sm"
            />
          </div>

          {/* Time Selection */}
          {selectedDate && (
            <div className="animate-fade-in mb-8">
              <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-primary-600" />
                Available Times *
              </label>
              {loadingSlots ? (
                <div className="text-center py-4">
                  <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm text-gray-600 mt-2">Loading available times...</p>
                </div>
              ) : availableSlots.length > 0 ? (
                <>
                  <div className="grid grid-cols-4 gap-3">
                    {availableSlots.map((time, index) => (
                      <button
                        key={time}
                        onClick={() => setSelectedTime(time)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all duration-300 transform hover:scale-105 hover:shadow-md ${
                          selectedTime === time
                            ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white border-primary-600 shadow-md scale-105'
                            : 'bg-white text-gray-700 border-gray-200 hover:bg-primary-50 hover:border-primary-300'
                        }`}
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                  {selectedTime && (
                    <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                       <div className="text-sm text-green-800">
                         <div className="font-medium mb-2 flex items-center">
                           <span className="mr-2">✓</span>
                           Time Selected
                         </div>
                         <div className="flex items-center justify-between">
                           <span>Selected time:</span>
                           <span className="font-medium">{selectedTime}</span>
                         </div>
                       </div>
                     </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No available times for this date</p>
                </div>
              )}
            </div>
          )}
        </div>

          {/* Contact Information */}
          <div className="space-y-3 bg-gradient-to-br from-gray-50 to-primary-50 p-4 rounded-lg border border-gray-200 shadow-sm mb-6">
            <div className="text-center mb-3">
              <div className="inline-flex items-center space-x-2 mb-1">
                <div className="w-6 h-6 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-sm">
                  <User className="w-3 h-3 text-white" />
                </div>
                <h3 className="text-base font-semibold text-gray-800">
                  Contact Information
                </h3>
              </div>
              <p className="text-gray-600 text-xs">
                Please provide your details to schedule the meeting
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                 <div className="group">
                   <label className="block text-sm font-medium text-gray-700 mb-1.5">
                     Full Name *
                   </label>
                   <input
                     type="text"
                     value={bookingForm.name}
                     onChange={(e) => setBookingForm(prev => ({ ...prev, name: e.target.value }))}
                     className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all duration-200 bg-white group-hover:border-gray-300 shadow-sm text-sm"
                     placeholder="Enter your full name"
                   />
                 </div>

                 <div className="group">
                   <label className="block text-sm font-medium text-gray-700 mb-1.5">
                     Email Address *
                   </label>
                   <input
                     type="email"
                     value={bookingForm.email}
                     onChange={(e) => setBookingForm(prev => ({ ...prev, email: e.target.value }))}
                     className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all duration-200 bg-white group-hover:border-gray-300 shadow-sm text-sm"
                     placeholder="your.email@company.com"
                   />
                 </div>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                 <div className="group">
                   <label className="block text-sm font-medium text-gray-700 mb-1.5">
                     Phone Number
                   </label>
                   <input
                     type="tel"
                     value={bookingForm.phone}
                     onChange={(e) => setBookingForm(prev => ({ ...prev, phone: e.target.value }))}
                     className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all duration-200 bg-white group-hover:border-gray-300 shadow-sm text-sm"
                     placeholder="+1 (555) 123-4567"
                   />
                 </div>

                 <div className="group">
                   <label className="block text-sm font-medium text-gray-700 mb-1.5">
                     Company
                   </label>
                   <input
                     type="text"
                     value={bookingForm.company}
                     onChange={(e) => setBookingForm(prev => ({ ...prev, company: e.target.value }))}
                     className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all duration-200 bg-white group-hover:border-gray-300 shadow-sm text-sm"
                     placeholder="Your company name"
                   />
                 </div>
               </div>

            <div className="group">
                 <label className="block text-sm font-medium text-gray-700 mb-1.5">
                   Meeting Type *
                 </label>
                 <select
                   value={bookingForm.meetingType}
                   onChange={(e) => setBookingForm(prev => ({ ...prev, meetingType: e.target.value }))}
                   className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all duration-200 bg-white group-hover:border-gray-300 cursor-pointer shadow-sm text-sm"
                 >
                   <option value="video">📹 Video Call</option>
                   <option value="call">📞 Phone Call</option>
                   <option value="in-person">🤝 In-Person Meeting</option>
                 </select>
               </div>

               <div className="group">
                 <label className="block text-sm font-medium text-gray-700 mb-1.5">
                   Message (Optional)
                 </label>
                 <textarea
                   value={bookingForm.message}
                   onChange={(e) => setBookingForm(prev => ({ ...prev, message: e.target.value }))}
                   rows={2}
                   className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all duration-200 bg-white group-hover:border-gray-300 resize-none shadow-sm text-sm"
                   placeholder="Tell us about your needs or any specific topics you'd like to discuss..."
                 />
               </div>
          </div>

          {/* Book Meeting Button */}
          <div className="mt-8 pt-4">
            <button
              onClick={handleBooking}
              disabled={loading || !selectedDate || !selectedTime || !bookingForm.name || !bookingForm.email}
              className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-4 px-6 rounded-lg font-semibold hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Scheduling Meeting...
                </>
              ) : (
                <>
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Meeting
                </>
              )}
            </button>
          </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Powered by {customization.companyName}
        </p>
      </div>
      
      {/* CSS Animations */}
       <style>{`
         @keyframes fadeIn {
           from {
             opacity: 0;
             transform: translateY(10px);
           }
           to {
             opacity: 1;
             transform: translateY(0);
           }
         }
         
         .animate-fade-in {
           animation: fadeIn 0.5s ease-in-out forwards;
         }
       `}</style>
    </div>
  );
};

export default PublicBookingWidget;